import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import React from "react";
import { FaShoppingBasket } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { colorBasketNumberBadgeSx } from "../../admin/jigsaw/play/AdminColorBasketMiniMap";
import UtilService from "../../services/UtilService";
import { usePagesTranslation } from "../locales";

const CARD_PAPER_SX = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    gap: { xs: 2, sm: 2.5 },
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    border: "1px solid rgba(255, 255, 255, 0.55)",
    background: "linear-gradient(145deg, rgba(255,255,255,0.58) 0%, rgba(248,252,249,0.52) 48%, rgba(238,248,242,0.48) 100%)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 40px rgba(27, 94, 32, 0.08), 0 2px 10px rgba(0, 0, 0, 0.04)",
    transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 14px 48px rgba(27, 94, 32, 0.12), 0 4px 14px rgba(0, 0, 0, 0.06)",
        borderColor: "rgba(255, 255, 255, 0.75)",
    },
};

const PREVIEW_BOX_SX = {
    position: "relative",
    width: { xs: "100%", sm: 320 },
    maxWidth: { xs: 440, sm: 320 },
    mx: { xs: "auto", sm: 0 },
    flexShrink: 0,
    aspectRatio: "1 / 1",
    borderRadius: 2,
    overflow: "hidden",
    bgcolor: "action.hover",
    border: "1px solid rgba(0,0,0,0.06)",
    transition: "transform 0.22s ease",
    ".MuiPaper-root:hover &": {
        transform: "scale(1.02)",
    },
};

const MORE_BUTTON_SX = {
    minWidth: 72,
    width: "100%",
    flexShrink: 0,
    fontWeight: 700,
    fontSize: "0.8125rem",
    textTransform: "none",
    borderColor: "rgba(59, 166, 102, 0.55)",
    color: "#2e7d32",
    "&:hover": {
        borderColor: "#2e7d32",
        bgcolor: "rgba(59, 166, 102, 0.08)",
    },
};

const PLAY_BUTTON_SX = {
    width: "100%",
    minHeight: 44,
    px: { xs: 1.5, sm: 2 },
    py: 1,
    fontWeight: 700,
    fontSize: "0.8125rem",
    textTransform: "none",
    flexShrink: 0,
    background: "linear-gradient(145deg, #2e7d32 0%, #3BA666 55%, #6bcf8e 100%)",
    color: "#fff",
    boxShadow: "0 8px 24px rgba(46, 125, 50, 0.38), 0 2px 8px rgba(0,0,0,0.08)",
    transition: "transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease",
    "&:hover": {
        background: "linear-gradient(145deg, #1b5e20 0%, #2e7d32 50%, #3BA666 100%)",
        boxShadow: "0 10px 28px rgba(27, 94, 32, 0.45)",
        transform: "scale(1.03)",
    },
    "&:active": {
        transform: "scale(0.98)",
    },
};

/** Distinct badge colors per puzzle kind for free-tier daily play. */
export const PUZZLE_KIND_BADGE_SX = {
    sliding: {
        bgcolor: "rgba(46, 125, 50, 0.14)",
        color: "#1b5e20",
        border: "1px solid rgba(46, 125, 50, 0.38)",
    },
    jigsaw: {
        bgcolor: "rgba(106, 27, 154, 0.12)",
        color: "#6a1b9a",
        border: "1px solid rgba(106, 27, 154, 0.35)",
    },
    crossword: {
        bgcolor: "rgba(230, 81, 0, 0.12)",
        color: "#e65100",
        border: "1px solid rgba(230, 81, 0, 0.35)",
    },
    hidden_message: {
        bgcolor: "rgba(21, 101, 192, 0.12)",
        color: "#1565c0",
        border: "1px solid rgba(21, 101, 192, 0.35)",
    },
    word_search: {
        bgcolor: "rgba(0, 105, 92, 0.12)",
        color: "#00695c",
        border: "1px solid rgba(0, 105, 92, 0.35)",
    },
};

const PREMIUM_BADGE_SX = {
    bgcolor: "rgba(245, 124, 0, 0.14)",
    color: "#e65100",
    border: "1px solid rgba(245, 124, 0, 0.45)",
};

const DAILY_FREE_BADGE_SX = {
    bgcolor: "rgba(59, 166, 102, 0.16)",
    color: "#1b5e20",
    border: "1px solid rgba(59, 166, 102, 0.42)",
};

function MetaGridIcon({ size = 14 }) {
    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            sx={{ display: "block", flexShrink: 0 }}
        >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
        </Box>
    );
}

function MetaLevelIcon({ size = 14 }) {
    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            sx={{ display: "block", flexShrink: 0 }}
        >
            <path d="M12 20V10" />
            <path d="M18 20V4" />
            <path d="M6 20v-4" />
        </Box>
    );
}

function MetaTimeIcon({ size = 12 }) {
    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            sx={{ display: "block", flexShrink: 0 }}
        >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </Box>
    );
}

function FeaturedPuzzleMetaRow({ gridSize, levelLabel }) {
    const itemSx = {
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: "rgba(30, 58, 47, 0.55)",
        fontSize: "0.8125rem",
        lineHeight: 1,
    };
    const labelSx = {
        fontWeight: 600,
        fontSize: "inherit",
        color: "rgba(30, 58, 47, 0.78)",
        lineHeight: 1.2,
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5 },
                mt: 0.5,
            }}
        >
            {gridSize != null ? (
                <Box sx={itemSx}>
                    <MetaGridIcon />
                    <Typography component="span" sx={labelSx}>
                        {gridSize}×{gridSize}
                    </Typography>
                </Box>
            ) : null}
            {levelLabel ? (
                <Box sx={itemSx}>
                    <MetaLevelIcon />
                    <Typography component="span" sx={labelSx}>
                        {levelLabel}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
}

function FeaturedPuzzleTimestamp({ publishedAt, lang }) {
    if (publishedAt == null) return null;
    const pubMs = new Date(publishedAt).getTime();
    if (!Number.isFinite(pubMs) || pubMs <= 0) return null;
    const ago = UtilService.formatHumanizeDetailed(Math.abs(Date.now() - pubMs), lang);
    if (!ago) return null;

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.45,
                mt: 0.75,
                color: "text.disabled",
                fontSize: "0.75rem",
                lineHeight: 1,
            }}
        >
            <MetaTimeIcon size={11} />
            <Typography component="span" sx={{ fontSize: "inherit", color: "inherit", fontWeight: 500 }}>
                {ago}
            </Typography>
        </Box>
    );
}

function FeaturedColorBasketsRow({ baskets }) {
    const { t } = usePagesTranslation();
    if (!Array.isArray(baskets) || baskets.length === 0) return null;
    return (
        <Box
            sx={{
                mt: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                alignItems: "flex-start",
            }}
            aria-label={t("landing.featured-color-baskets-aria", "Color baskets")}
        >
            {baskets.map((basket) => {
                const hex = basket?.dominantColorHex || "#999999";
                const pieceIds = Array.isArray(basket?.pieceIds) ? basket.pieceIds : [];
                const pieceCount = pieceIds.length;
                const n = Number(basket?.index);
                const displayN = Number.isFinite(n) ? n + 1 : "?";
                return (
                    <Box
                        key={`featured-basket-${basket?.index ?? hex}`}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.25,
                            width: 40,
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                bgcolor: hex,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 2,
                                    left: 2,
                                    ...colorBasketNumberBadgeSx({ size: 14 }),
                                }}
                            >
                                {displayN}
                            </Box>
                            <FaShoppingBasket size={14} color="#fff" aria-hidden />
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: "text.secondary",
                                fontSize: 9,
                                lineHeight: 1.15,
                                textAlign: "center",
                            }}
                        >
                            {t("landing.featured-basket-pieces", {
                                count: pieceCount,
                                defaultValue: "{{count}} pcs",
                            })}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

export default function LandingFeaturedPuzzleCard({
    puzzleKind,
    badgeLabel,
    isFreeTier = true,
    title,
    gridSize = null,
    levelLabel,
    publishedAt,
    subtitle,
    subtitleDense = false,
    playLabel,
    playAria,
    onPlay,
    morePath,
    moreLabel,
    preview,
    placeholderIcon: PlaceholderIcon,
    appStoreLink = null,
    lang,
    colorBaskets = null,
}) {
    const badgeSx = isFreeTier
        ? DAILY_FREE_BADGE_SX
        : PREMIUM_BADGE_SX;

    return (
        <Paper elevation={0} sx={CARD_PAPER_SX}>
            <Box sx={PREVIEW_BOX_SX}>
                {preview || (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.disabled",
                        }}
                    >
                        <PlaceholderIcon sx={{ fontSize: 48, opacity: 0.35 }} />
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    alignSelf: "stretch",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: { xs: 1.5, sm: 2 },
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Chip
                            label={badgeLabel}
                            size="small"
                            sx={{
                                mb: 1,
                                height: 26,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                ...badgeSx,
                            }}
                        />
                        <Typography
                            variant="subtitle1"
                            component="h3"
                            sx={{
                                fontWeight: 700,
                                lineHeight: 1.35,
                                mb: 0.25,
                                fontSize: { xs: "1.05rem", sm: "1.15rem" },
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {title}
                        </Typography>
                        <FeaturedPuzzleMetaRow gridSize={gridSize} levelLabel={levelLabel} />
                        <FeaturedPuzzleTimestamp publishedAt={publishedAt} lang={lang} />
                        {subtitle ? (
                            subtitleDense ? (
                                subtitle
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 0.75,
                                        display: { xs: "none", sm: "block" },
                                        lineHeight: 1.5,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            )
                        ) : null}
                        <FeaturedColorBasketsRow baskets={colorBaskets} />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            justifyContent: "center",
                            gap: 1,
                            flexShrink: 0,
                            width: { xs: "100%", sm: 132 },
                            minWidth: { sm: 132 },
                        }}
                    >
                        {morePath ? (
                            <Button
                                component={RouterLink}
                                to={morePath}
                                variant="outlined"
                                size="small"
                                sx={MORE_BUTTON_SX}
                            >
                                {moreLabel}
                            </Button>
                        ) : null}
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onPlay}
                            aria-label={playAria}
                            startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                            sx={PLAY_BUTTON_SX}
                        >
                            {playLabel}
                        </Button>
                    </Box>
                </Box>
                {appStoreLink ? (
                    <Box sx={{ width: "100%", minWidth: 0 }}>
                        {appStoreLink}
                    </Box>
                ) : null}
            </Box>
        </Paper>
    );
}
